import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, MEDIA } from "@/lib/api";
import { CardMedia } from "@/components/CardImage";
import { ConditionBadge, Tag } from "@/components/Badges";
import { Loading, ErrorState } from "@/components/States";
import { ReportDialog } from "@/components/ReportDialog";
import { ProposalComposer } from "@/components/ProposalComposer";
import { useAuth } from "@/context/AuthContext";
import { ReputationSummary, Stars } from "@/components/reputation/ReputationSummary";
import { Button } from "@/components/ui/button";
import { Star, MapPin, CalendarDays, ShieldCheck, Ban, MessageSquare, Repeat } from "lucide-react";
import { toast } from "sonner";

const MODE_LABEL = { shipping: "Spedizione", in_person: "Di persona" };

export default function PublicProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [fb, setFb] = useState(null);
  const [error, setError] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => {
    setP(null); setError(null);
    api.get(`/profiles/${username}`).then((r) => setP(r.data)).catch(() => setError("Profilo non trovato."));
    api.get(`/feedback/user/${username}`).then((r) => setFb(r.data)).catch(() => {});
  }, [username]);

  const block = async () => {
    try { await api.post("/blocks", { username }); toast.success("Utente bloccato. Non potrete più contattarvi né apparire nei reciproci match."); }
    catch { toast.error("Operazione non riuscita."); }
  };

  if (error) return <ErrorState message={error} />;
  if (!p) return <Loading />;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-col gap-4 rounded-2xl border border-[#2A3655] bg-[#141B2D] p-6 sm:flex-row sm:items-start">
        <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl bg-[#7C3AED] text-2xl font-bold text-white">
          {p.avatar_path ? <img src={MEDIA(p.avatar_path)} alt="" className="h-full w-full object-cover" /> : p.username[0].toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-2xl font-bold text-[#F8FAFC]">@{p.username}</h1>
            {p.email_verified && <Tag tone="#10B981" testid="email-verified-badge"><ShieldCheck className="mr-1 h-3 w-3" />Email verificata</Tag>}
          </div>
          {p.bio && <p className="mt-2 text-sm text-[#CBD5E1]">{p.bio}</p>}
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#94A3B8]">
            {p.province && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.province} ({p.country})</span>}
            <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />Iscritto dal {new Date(p.created_at).toLocaleDateString("it-IT")}</span>
            <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-[#F59E0B]" />{p.feedback_count ? `${p.feedback_average} (${p.feedback_count} recensioni)` : "Nessuna recensione ancora"}</span>
            <span>Scambi completati: {p.completed_trades}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-1">
            {(p.preferred_tcgs || []).map((t) => <Tag key={t} tone="#7C3AED">{t === "pokemon" ? "Pokémon" : t === "one_piece" ? "One Piece" : t}</Tag>)}
            {(p.trade_modes || []).map((m) => <Tag key={m} tone="#2DD4BF">{MODE_LABEL[m] || m}</Tag>)}
          </div>
        </div>
        {user && user.username !== p.username && (
          <div className="flex flex-col gap-2">
            <Button onClick={() => setComposerOpen(true)} size="sm" data-testid="profile-open-proposal" className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Repeat className="mr-2 h-4 w-4" />Proponi uno scambio</Button>
            <Button asChild size="sm" variant="outline" className="border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]"><Link to={`/match/${p.id}`} data-testid="profile-view-match"><MessageSquare className="mr-2 h-4 w-4" />Vedi corrispondenze</Link></Button>
            <button onClick={block} data-testid="profile-block" className="flex items-center justify-center gap-1 text-xs text-[#94A3B8] hover:text-[#EF4444]"><Ban className="h-3.5 w-3.5" />Blocca</button>
            <ReportDialog targetType="user" targetId={p.id} />
          </div>
        )}
      </div>

      {user && user.username !== p.username && (
        <ProposalComposer open={composerOpen} onOpenChange={setComposerOpen} toUserId={p.id} toUsername={p.username} onSent={(d) => nav(`/proposte/${d.id}`)} />
      )}

      <ReputationSummary p={p} />

      <h2 className="mt-8 font-display text-xl font-semibold text-[#F8FAFC]">Carte pubblicate</h2>
      {p.cards.length === 0 ? (
        <p className="mt-3 rounded-xl border border-[#2A3655] bg-[#141B2D] p-6 text-center text-sm text-[#94A3B8]">Questo collezionista non ha ancora carte pubbliche.</p>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {p.cards.map((c) => (
            <div key={c.id} className="rounded-xl border border-[#2A3655] bg-[#141B2D] p-2">
              <CardMedia photos={c.photos} card={c.card} />
              <p className="mt-2 line-clamp-1 px-1 text-sm font-semibold text-[#F8FAFC]">{c.card?.name}</p>
              <div className="flex flex-wrap gap-1 px-1 pb-1 pt-1"><ConditionBadge value={c.condition} /><Tag>{c.language}</Tag></div>
            </div>
          ))}
        </div>
      )}

      <h2 className="mt-8 font-display text-xl font-semibold text-[#F8FAFC]">Feedback ricevuti ({fb?.count || 0})</h2>
      {(!fb || fb.count === 0) && <p className="mt-3 rounded-xl border border-[#2A3655] bg-[#141B2D] p-6 text-center text-sm text-[#94A3B8]" data-testid="feedback-empty">Questo collezionista non ha ancora recensioni.</p>}
      {fb && fb.count > 0 && (
        <>
          <div className="mt-4 space-y-2" data-testid="feedback-list">
            {fb.items.map((f) => (
              <div key={f.id} className="rounded-xl border border-[#2A3655] bg-[#141B2D] p-4" data-testid="feedback-item">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#F8FAFC]"><Stars value={f.rating} />{f.rating}/5 · @{f.from_username}</span>
                  <ReportDialog targetType="feedback" targetId={f.id} label="" />
                </div>
                <p className="mt-1 text-[11px] text-[#10B981]">Scambio completato{f.trade_date ? ` il ${new Date(f.trade_date).toLocaleDateString("it-IT")}` : ""}</p>
                {f.comment && <p className="mt-1 text-sm text-[#CBD5E1]">{f.comment}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
